import { signal } from '@preact/signals';

/**
 * Menu UI scaling for short screens. A phone browser in landscape keeps only ~280–340 px of height
 * once its toolbars are drawn, less than the compact menu layout needs. Below MENU_DESIGN_H the menus
 * lay out at that height (wider in proportion) and `.ui-root` scales them down to fit (ui.css).
 * Menu stylesheets size against this virtual viewport through `@container ui` queries; components
 * read `uiViewport`. The match view is not scaled: its touch buttons shrink by `hudFit` instead and
 * hud.css has a short-screen tier for the rest of the HUD.
 */
export const MENU_DESIGN_H = 420;
/** Smallest scale (≈ 280 px tall): below this, text gets too small to read on a phone. */
const MIN_SCALE = 0.66;
/** The match's touch buttons are laid out for phones this tall (fullscreen landscape). */
const HUD_DESIGN_H = 390;

export interface UiViewport {
  /** Virtual (layout) size of the menu UI in CSS px. */
  w: number;
  h: number;
  /** Screen px per menu px (1 = unscaled). */
  scale: number;
  /** Size factor for the match's touch buttons so the cluster fits short screens (≤ 1). */
  hudFit: number;
}

const measure = (): UiViewport => {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const scale = h > 0 && h < MENU_DESIGN_H ? Math.max(MIN_SCALE, Math.round((h / MENU_DESIGN_H) * 1000) / 1000) : 1;
  const hudFit = h > 0 && h < HUD_DESIGN_H ? Math.max(0.68, Math.round((h / HUD_DESIGN_H) * 1000) / 1000) : 1;
  return { w: Math.round(w / scale), h: Math.round(h / scale), scale, hudFit };
};

export const uiViewport = signal<UiViewport>(measure());

function update(): void {
  const v = measure();
  const cur = uiViewport.value;
  document.documentElement.style.setProperty('--ui-s', String(v.scale));
  if (v.w !== cur.w || v.h !== cur.h || v.scale !== cur.scale || v.hudFit !== cur.hudFit) uiViewport.value = v;
}

update();
addEventListener('resize', update);
window.visualViewport?.addEventListener('resize', update);
// iOS reports the new size a moment after the rotation event.
addEventListener('orientationchange', () => {
  update();
  setTimeout(update, 250);
  setTimeout(update, 700);
});

/**
 * Converts a client (screen) point to CSS px inside `el`, undoing any scale between them
 * (the menu UI is scaled on short screens).
 */
export function localPoint(el: Element, rect: DOMRect, clientX: number, clientY: number): { x: number; y: number } {
  const he = el as HTMLElement;
  const kx = rect.width > 0 && he.offsetWidth > 0 ? he.offsetWidth / rect.width : 1;
  const ky = rect.height > 0 && he.offsetHeight > 0 ? he.offsetHeight / rect.height : 1;
  return { x: (clientX - rect.left) * kx, y: (clientY - rect.top) * ky };
}
